import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import get from "lodash/get";

import Context from "shared/util/Context";
import isSpa from "shared/util/isSpa";
import Link from "shared/components/Link";
import Str from "shared/components/Str";
import ClickWatcher from "shared/components/ClickWatcher";
import noop from "shared/util/noop";
import { selectedOptionOnNavBarEvent } from "shared/components/NavBar/analytics";
import {
	OKEVENT,
	subscribeToOkEvent,
	unsubscribeFromOkEvent,
} from "shared/util/OkEventHelpers";

import { MESSAGE_FOLDERS } from "apps/messages/util/constants";
import Messages from "apps/messages/components/Messages";
import ErrorMessage from "apps/messages/components/ErrorMessage";

import "./Dropdown.scss";

const SCROLL_ID = "messagesdropdown-scroll-container";
const QUERY = gql`
	${Messages.fragments.conversations}

	query getIncomingMessages (
		$userid: String!,
		$conversationsFilter: ConversationFilter!
		$conversationsAfter: String
	) {
		user(id: $userid) {
			id
			...Conversations
		}
	}
`;

const Dropdown = ({ isOpen, setIsOpen }) => {
	const close = useCallback(() => setIsOpen(false), [setIsOpen]);
	const { data, loading, error } = useQuery(QUERY, {
		variables: {
			userid: Context.get("userid"),
			conversationsFilter: MESSAGE_FOLDERS.INCOMING,
		},
		fetchPolicy: "cache-and-network",
	});

	const [hiddenUsers, setHiddenUsers] = useState({});
	useEffect(() => {
		const hideUser = (params) => {
			setHiddenUsers({
				...hiddenUsers,
				[params.tuid]: true,
			});
		};

		subscribeToOkEvent(OKEVENT.Unmatch, hideUser);
		subscribeToOkEvent(OKEVENT.Block, hideUser);

		return () => {
			if (isOpen) {
				unsubscribeFromOkEvent(OKEVENT.Unmatch, hideUser);
				unsubscribeFromOkEvent(OKEVENT.Block, hideUser);
			}
		};
	}, [isOpen, close, hiddenUsers, setHiddenUsers]);

	useEffect(() => {
		window.ScrollHelpers.preventOverscroll(SCROLL_ID);
	}, []);

	if (!isOpen) {
		return null;
	}

	const user = get(data, "user") || {};

	return (
		<ClickWatcher
			className="messagedropdown"
			onOutsideClick={close}
			active={isOpen}
		>
			<div className="messagedropdown-body" id={SCROLL_ID}>
				{
					error
						? (
							<ErrorMessage>
								<Str>There was a problem loading your messages!</Str>
							</ErrorMessage>
						)
						: (
							<Messages
								filter={MESSAGE_FOLDERS.INCOMING}
								fetchMore={noop}
								conversations={user.conversations}
								hiddenUsers={hiddenUsers}
								loading={loading}
								onMessageOpen={close}
								hasInfiniteScroll={false}
							/>
						)
				}
			</div>

			<Link
				to="/messages"
				className="messagedropdown-see-all"
				onClick={() => {
					selectedOptionOnNavBarEvent("Messages", "Conversations Dropdown");
					close();
				}}
				noSpa={!isSpa()}
			>
				<Str>See all</Str>
			</Link>
		</ClickWatcher>
	);
};

Dropdown.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	setIsOpen: PropTypes.func.isRequired,
};

export default Dropdown;



// WEBPACK FOOTER //
// ./src/apps/messages/components/Dropdown.jsx